import { Briefcase } from "lucide-react";
import { useTranslations } from "use-intl";
import {
  CallPropertyTag,
  type CallWaiting,
  IncomingCallActions,
  OnCallStrip,
} from "@/components/communications/call-waiting";
import { PersonAvatar } from "@/components/person-avatar";
import { useApp } from "@/lib/context/use-powersync";
import { useAllProperties } from "@/lib/hooks/use-all-properties";
import { useStaffCallerLookup } from "@/lib/hooks/use-staff-caller-lookup";
import {
  cn,
  getInitialsFromDisplayName,
  getStaffDisplayName,
} from "@/lib/utils";

/**
 * Internal-call counterpart to <IncomingCallView>. Same layout, but:
 *   - looks the caller up via membership → staff instead of phone → guest
 *   - shows an "Internal" label instead of "Phone"/"WhatsApp"
 *   - no reservation/unit pill (staff don't have one)
 *
 * Rendered when `pendingInvite.kind === "staff"`. The caller's membership
 * id is decoded from the Twilio identity at invite time and stored on the
 * PendingInvite — we just consume it here.
 */
export interface IncomingStaffCallViewProps {
  onAccept: () => void;
  onReject: () => void;
  /** The property the call rang for, decoded from the caller's identity. */
  propertyId: string | null | undefined;
  /** Caller's membership id, decoded from their `<member>__<property>` identity. */
  staffMemberId: string | undefined;
  /** The name the call carried, shown until the caller's profile syncs. */
  staffName?: string;
  waiting?: CallWaiting;
}

export function IncomingStaffCallView({
  staffMemberId,
  staffName,
  onAccept,
  onReject,
  propertyId,
  waiting,
}: IncomingStaffCallViewProps) {
  const t = useTranslations("communications.incomingStaffCallView");
  const { currentProperty } = useApp();
  const { staff } = useStaffCallerLookup(staffMemberId);
  const properties = useAllProperties();

  const callerName = (staff && getStaffDisplayName(staff)) || staffName;
  const displayName = callerName ?? t("internalCall");
  const initials = callerName ? getInitialsFromDisplayName(callerName) : "";
  const avatarSrc = staff?.image ?? undefined;

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        "bg-gradient-to-b from-primary/10 via-background to-background",
        "px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]"
      )}
    >
      {waiting ? (
        <OnCallStrip
          call={waiting.liveCall}
          heldCall={waiting.heldCall}
          onHangUp={waiting.onHangUp}
        />
      ) : null}

      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        <Briefcase aria-hidden="true" className="h-3.5 w-3.5" />
        <p className="text-xs font-medium tracking-[0.18em] uppercase">
          {t("header")}
        </p>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="relative flex h-32 w-32 items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-primary/30 motion-safe:animate-ping"
          />
          <span
            aria-hidden="true"
            className="absolute inset-2 rounded-full bg-primary/20 [animation-delay:600ms] motion-safe:animate-ping"
          />
          <PersonAvatar
            alt={displayName}
            avatar={avatarSrc}
            className="relative z-10 shadow-xl"
            initials={initials}
            size="2xl"
          />
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-3xl font-semibold text-foreground">
            {displayName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {currentProperty?.name && properties.length < 2
              ? t("internalAtProperty", { property: currentProperty.name })
              : t("internal")}
          </p>
          <CallPropertyTag
            className="rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-foreground shadow-sm"
            propertyId={propertyId}
          />
        </div>
      </div>

      <IncomingCallActions
        choices={waiting?.choices}
        onAccept={onAccept}
        onReject={onReject}
        testIdPrefix="incoming-staff-call"
      />
    </div>
  );
}
